import { useEffect, useRef, useState } from 'react'
import { IconChip } from '#app/components/ui/icon-chip.tsx'
import { type IconName } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

export type Chapter = {
	key: string
	eyebrow: string
	title: string
	/** Optional: a chapter may be told by its title alone. */
	lead?: string
	/** Up to four; the first leads as the gradient chip. */
	icons: IconName[]
	/** Without one, the chapter's own eyebrow, title and lead stay visible
	 * beside the panel. */
	body?: React.ReactNode
}

/** A chapter or row ordinal as the index shows it: 01, 02, ... */
export function chapterNumber(index: number) {
	return String(index + 1).padStart(2, '0')
}

/**
 * ChapterStage: chapters told side by side with a pinned panel. From `lg` a
 * sticky panel on the left holds the chapter index and the active chapter's
 * eyebrow, title, lead if any and icon chips, which cross-fade as the visitor
 * scrolls the chapter bodies on the right. A chapter is active while it
 * covers the viewport middle.
 *
 * Each chapter keeps its own eyebrow, heading and lead (if any) above its body:
 * visible below `lg`, `sr-only` from `lg` unless the chapter has no body. So
 * assistive tech always meets one heading per chapter, in order and above
 * that chapter's content, and the panel copy, which repeats the active one,
 * is `aria-hidden`. The chapters are h2s, or h3s under a section's h2.
 */
export function ChapterStage({
	chapters,
	indexLabel,
	headingLevel: Heading = 'h2',
	className,
}: {
	chapters: Chapter[]
	/** Accessible name of the chapter index navigation. */
	indexLabel: string
	headingLevel?: 'h2' | 'h3'
	className?: string
}) {
	const headingSize = Heading === 'h2' ? 'text-h2' : 'text-h3'

	const [active, setActive] = useState(0)
	const chapterRefs = useRef<(HTMLElement | null)[]>([])

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const index = (entry.target as HTMLElement | undefined)?.dataset
						?.index
					if (entry.isIntersecting && index) setActive(Number(index))
				}
			},
			{ rootMargin: '-45% 0px -45% 0px' },
		)
		for (const element of chapterRefs.current) {
			if (element) observer.observe(element)
		}
		return () => observer.disconnect()
	}, [chapters.length])

	function scrollToChapter(index: number) {
		const reduced = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches
		chapterRefs.current[index]?.scrollIntoView({
			behavior: reduced ? 'auto' : 'smooth',
			block: 'center',
		})
	}

	const current = chapters[active] ?? chapters[0]
	if (!current) return null

	return (
		<div
			data-motion="chapter-stage"
			className={cn(
				'grid grid-cols-1 gap-16 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]',
				className,
			)}
		>
			<div
				data-slot="chapter-panel"
				className="hidden min-w-0 self-start lg:sticky lg:top-28 lg:flex lg:h-[calc(100vh-8rem)] lg:flex-col lg:justify-between"
			>
				<nav aria-label={indexLabel}>
					<ol className="flex flex-col gap-3">
						{chapters.map((chapter, i) => (
							<li key={chapter.key}>
								<button
									type="button"
									aria-current={i === active ? 'true' : undefined}
									onClick={() => scrollToChapter(i)}
									className={cn(
										'text-body-xs focus-cosy duration-medium flex w-full items-center gap-4 rounded-sm text-left font-mono transition-colors',
										i === active
											? 'text-foreground'
											: 'text-muted-foreground hover:text-foreground',
									)}
								>
									<span aria-hidden="true" className="tabular-nums">
										{chapterNumber(i)}
									</span>
									<span
										aria-hidden="true"
										className="bg-border relative h-px flex-1 overflow-hidden"
									>
										<span
											className={cn(
												'bg-primary ease-out-expo duration-medium absolute inset-0 origin-left transition-transform',
												i <= active ? 'scale-x-100' : 'scale-x-0',
											)}
										/>
									</span>
									<span>{chapter.title}</span>
								</button>
							</li>
						))}
					</ol>
				</nav>

				{/* Keyed by chapter, so a change remounts it and replays the
				    animations. */}
				<div
					key={current.key}
					data-slot="chapter-panel-copy"
					aria-hidden="true"
					className="flex flex-col gap-10"
				>
					<div className="animate-rise-in">
						<ChapterEyebrow>{current.eyebrow}</ChapterEyebrow>
						<p className="text-h1 mt-4 font-bold tracking-tight break-words hyphens-auto">
							<span className="text-gradient-brand">{current.title}</span>
						</p>
						{current.lead ? (
							<p className="text-body-md text-muted-foreground mt-6 max-w-md">
								{current.lead}
							</p>
						) : null}
					</div>
					<div className="flex gap-3">
						{current.icons.slice(0, 4).map((icon, i) => (
							<IconChip
								key={i}
								icon={icon}
								size="xl"
								variant={i === 0 ? 'gradient' : 'soft'}
								className="animate-pop-in"
								style={{ animationDelay: `${120 + i * 90}ms` }}
							/>
						))}
					</div>
				</div>
			</div>

			<div className="flex min-w-0 flex-col gap-24 lg:gap-32">
				{chapters.map((chapter, i) => (
					<div
						key={chapter.key}
						data-slot="chapter"
						data-index={i}
						ref={(el) => {
							chapterRefs.current[i] = el
						}}
						// The panel spans from 7rem to 1rem above the viewport bottom.
						// A last chapter centred in the viewport must reach that low, or
						// the grid ends and carries the panel off: 100vh - 2rem, plus a
						// rem of slack.
						className="min-w-0 lg:last:min-h-[calc(100vh-1rem)]"
					>
						<div className={chapter.body ? 'mb-8 lg:sr-only' : undefined}>
							<ChapterEyebrow>{chapter.eyebrow}</ChapterEyebrow>
							{/* Like the panel title: a long German compound outgrows a
							    phone column unless it may hyphenate. */}
							<Heading
								className={cn(
									headingSize,
									'mt-2 font-bold break-words hyphens-auto',
								)}
							>
								{chapter.title}
							</Heading>
							{chapter.lead ? (
								<p className="text-body-md text-muted-foreground mt-3">
									{chapter.lead}
								</p>
							) : null}
						</div>
						{chapter.body}
					</div>
				))}
			</div>
		</div>
	)
}

function ChapterEyebrow({ children }: { children: React.ReactNode }) {
	return (
		<p className="text-body-sm text-primary font-semibold tracking-wide uppercase">
			{children}
		</p>
	)
}
